import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useLocation } from 'react-router-dom'
import { toggleUploadDialog } from '../../store/slice/dialogSlice'
import { useGetRequestByTypeQuery } from '../../store/api/requestApi'
import { CloudDownload, DeleteOutlined } from '@mui/icons-material'
import GetAppIcon from '@mui/icons-material/GetApp'
import { toast } from 'react-toastify'
import { useUploadFileMutation } from '../../store/api/uploadApi'
import { updateAttachmentCount } from '../../store/slice/mainToCardSlice'
import { isEmpty } from 'lodash'
import { isPendingAttachment, validateAttachments, isSameAttachmentList } from './attachmentValidation'

const UploadDialog = ({ module, value, setValue, initialData, onlyView }) => {
  const inputRef = useRef()
  const autoAttachIdRef = useRef(0)
  const [files, setFiles] = useState([])
  const [newFilesToUpload, setNewFilesToUpload] = useState([])
  const open = useSelector((state) => state.dialogSlice.attachment)
  const buData = useSelector((state) => state.persistSlice.buData)
  const { data: reqTypeData } = useGetRequestByTypeQuery(buData)
  const dispatch = useDispatch()

  const [postData, { data: postedData, isLoading: isUploading, isError: uploadError }] = useUploadFileMutation()

  const handleClose = () => {
    if (isUploading) {
      toast.warning('Upload is still in progress. Please wait.')
      return
    }
    // fileLink-i olmayan attachment ilə bağlamağa icazə vermirik
    if (!validateAttachments((files || []).filter((f) => f.type !== 2))) return

    dispatch(toggleUploadDialog())
  }

  useEffect(() => {
    // initialData valideyndə hər render-də yeni massiv kimi yarana bilir.
    // Məzmun eynidirsə state-i dəyişmirik: əks halda
    // setFiles -> setValue -> valideyn state -> yeni initialData -> setFiles ... sonsuz dövrə yaranır.
    const next = initialData || []
    setFiles((prev) => (isSameAttachmentList(prev, next) ? prev : next))
  }, [initialData])

  // yalnız yeni seçilən batch upload olunur (bütün siyahı yox)
  useEffect(() => {
    if (newFilesToUpload.length === 0) return

    const formData = new FormData()
    formData.append('module', module)
    newFilesToUpload.forEach((item) => formData.append('files', item.file))

    postData(formData)
  }, [newFilesToUpload])

  useEffect(() => {
    if (newFilesToUpload.length === 0) return
    if (!postedData && !uploadError) return

    // Linkləri göndərilən batch-in sırasına görə uyğunlaşdırırıq.
    // (attachmentId-nin mütləq qiyməti ilə indeksləmək ikinci batch-də sürüşür və fileLink boş qalırdı)
    const linkByAttachmentId = new Map()
    if (postedData?.message === 'Request succeeded') {
      newFilesToUpload.forEach((item, index) => {
        const link = postedData?.data?.[index]
        if (link) linkByAttachmentId.set(item.attachmentId, link)
      })
    }

    const failed = newFilesToUpload.filter((item) => !linkByAttachmentId.has(item.attachmentId))

    // link almayan faylları siyahıda saxlamırıq ki, fileLink = null save-ə getməsin
    const updatedFiles = (files || [])
      .filter((file) => !failed.some((item) => item.attachmentId === file.attachmentId))
      .map((file) =>
        linkByAttachmentId.has(file.attachmentId)
          ? { ...file, fileLink: linkByAttachmentId.get(file.attachmentId) }
          : file
      )

    if (failed.length > 0) {
      toast.error(`Upload failed for: ${failed.map((item) => item.name).join(', ')}. Please try again.`)
    }

    setFiles(updatedFiles)
    setValue(updatedFiles)
    setNewFilesToUpload([])
  }, [postedData, uploadError])

  let content, action
  const url = useLocation().pathname

  const handleDownload = async (file) => {
    const link = document.createElement('a')
    link.href = file.fileLink
    link.setAttribute('download', file.name)
    document.body.appendChild(link)
    link.click()
  }

  const handleDelete = (file) => {
    let newFiles = [...files]
    const updatedFile = newFiles.map((item) => {
      if (item.attachmentId === file.attachmentId && file.type !== 2) {
        return { ...item, type: 2 }
      }
      return item
    })
    if (inputRef.current) inputRef.current.value = ''
    setFiles(updatedFile)
    setValue(updatedFile)
  }

  const handleFileUpload = (file) => {
    if (!file) {
      return
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.warning(`File size cannot exceed 50MB `)
      return
    }

    const isDuplicateFile = files?.filter((f) => f.type !== 2).some((existingFile) => existingFile.name === file.name)

    if (isDuplicateFile) {
      toast.warning(`A file with the same name already exists.`)
      return
    }

    autoAttachIdRef.current--

    const newFile = {
      attachmentId: autoAttachIdRef.current,
      name: file.name,
      sourceId: 0,
      sourceTypeId: 0,
      reference: '',
      extensionType: file.type,
      attachmentTypeId: 0,
      attachmentSubTypeId: 0,
      lastModifiedDate: file.lastModifiedDate.toISOString(),
      size: file.size,
      file: file,
      fileLink: '',
      type: 0,
    }

    setFiles((prevFiles) => [...(prevFiles || []), newFile])
    setNewFilesToUpload((prev) => [...prev, newFile])
  }

  const handleFileSelect = (e) => {
    const selected = e.target.files

    if (selected) {
      Array.from(selected).forEach((file) => handleFileUpload(file))
    }
    e.target.value = ''
  }


  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle sx={{ backgroundColor: '#1976d2', color: '#fff' }}>{module} Attachments</DialogTitle>
      <Divider />

      {!onlyView && (
        <div className="uploadArea">
          <Button variant="contained" component="label" endIcon={<CloudDownload />} disabled={isUploading}>
            Upload
            <input
              onChange={handleFileSelect}
              ref={inputRef}
              hidden
              type="file"
              multiple
              accept=".xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.pdf"
            />
          </Button>
        </div>
      )}

      <DialogContent>
        <TableContainer component={Paper}>
          <Table>
            <TableHead variant="head">
              <TableRow>
                <TableCell sx={{ paddingLeft: '.5rem !important' }}>File Name</TableCell>
                <TableCell sx={{ paddingLeft: '.5rem !important' }}>Size</TableCell>
                <TableCell sx={{ paddingLeft: '.5rem !important' }}>Status</TableCell>
                <TableCell sx={{ paddingLeft: '.5rem !important' }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files &&
                files?.filter((f) => f.type !== 2).length > 0 &&
                files
                  ?.filter((f) => f.type !== 2)
                  .map((file, index) => (
                    <TableRow key={index} hover={true}>
                      <TableCell component="th" scope="row" size="small" sx={{ padding: '.5rem !important' }}>
                        {file.name}
                      </TableCell>
                      <TableCell component="th" scope="row" size="small" sx={{ padding: '.5rem !important' }}>
                        {file.size}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        size="small"
                        sx={{ padding: '.5rem !important', color: isPendingAttachment(file) ? 'error.main' : 'success.main' }}
                      >
                        {isPendingAttachment(file) ? 'Uploading...' : 'Uploaded'}
                      </TableCell>
                      <TableCell align="right" size="small" sx={{ padding: '.5rem !important' }}>
                        <Button variant="contained" color="primary" onClick={() => handleDownload(file)}>
                          <GetAppIcon />
                        </Button>
                        {!onlyView && (
                          <Button
                            // className={reviseId && reviseId !== '0' ? 'non-editable-shadow' : ''}
                            variant="contained"
                            color="secondary"
                            style={{ marginLeft: '15px', width: '20px' }}
                            onClick={(e) => handleDelete(file)}
                          >
                            <DeleteOutlined />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UploadDialog
